import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useEffect, useState } from "react";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import BarSpinner from "../../Components/Loader/BarSpinner.tsx";
import { Category } from "../../models/category";
import { CategoryType } from "../../models/category-type.ts";
import {
  addCategory,
  deleteCategory,
  getCategoriesWithQuantities,
  updateCategory,
} from "../../Services/category.service.ts";
import styles from "./CategoryList.module.css";

const CategoryList = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(
    null
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const fetchCategories = async () => {
    setLoading(true);
    const response = await getCategoriesWithQuantities();
    if (response.success && Array.isArray(response.data)) {
      setCategories(response.data);
    } else {
      setCategories([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const resetPopup = () => {
    setEditingCategory(null);
    setShowPopup(false);
    setError("");
  };

  const handleAddOrEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory) return;

    const result = editingCategory.id
      ? await updateCategory(editingCategory.id, editingCategory.name)
      : await addCategory(editingCategory.name);

    if (typeof result === "string") {
      setError(result);
    } else {
      setSnackbar({
        open: true,
        message: editingCategory.id
          ? "Category updated successfully."
          : "Category added successfully.",
        severity: "success",
      });
      fetchCategories();
      resetPopup();
    }
  };

  const handleEdit = (cat: Category) => {
    setEditingCategory(cat);
    setShowPopup(true);
    setError("");
  };

  const handleDelete = (id: string) => {
    setDeletingCategoryId(id);
    setShowDeletePopup(true);
  };

  const confirmDelete = async () => {
    if (!deletingCategoryId) return;
    const result = await deleteCategory(deletingCategoryId);
    if (typeof result === "string") {
      setSnackbar({
        open: true,
        message: "Failed to delete category.",
        severity: "error",
      });
    } else {
      setSnackbar({
        open: true,
        message: "Category deleted successfully.",
        severity: "success",
      });
      fetchCategories();
    }
    setShowDeletePopup(false);
    setDeletingCategoryId(null);
  };

  const resetDeletePopup = () => {
    setShowDeletePopup(false);
    setDeletingCategoryId(null);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <div className={styles.titleSection}>
          <h3 className={styles.title}>Categories</h3>
          <p className={styles.subtitle}>
            Track your categories, activated or deactivated them
          </p>
          <p className={styles.totalCount}>
            Total {categories.length} categories
          </p>
        </div>
        <button
          className={styles.addBtn}
          onClick={() => {
            setEditingCategory({
              name: "" as CategoryType,
              createdAt: new Date(),
              updatedAt: new Date(),
              quantity: 0,
            });
            setShowPopup(true);
          }}
        >
          + Add new category
        </button>
      </div>

      <hr className={styles.divider} />

      <div className={styles.tableWrapper}>
        {loading ? (
          <div className={styles.loader}>
            <BarSpinner />
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.numberColumn}>#</th>
                <th className={styles.categoryColumn}>Category</th>
                <th className={styles.quantityColumn}>Quantity</th>
                <th className={styles.createdAtColumn}>Created At</th>
                <th className={styles.updatedAtColumn}>Updated At</th>
                <th className={styles.actionColumn}>Action</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat, index) => (
                <tr key={cat.id}>
                  <td className={styles.numberCell}>
                    <span className={styles.numberBadge}>{index + 1}</span>
                  </td>
                  <td className={styles.categoryCell}>{cat.name}</td>
                  <td className={styles.quantityCell}>{cat.quantity}</td>
                  <td className={styles.createdAtCell}>
                    {cat.createdAt
                      ? new Date(cat.createdAt).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td className={styles.updatedAtCell}>
                    {cat.updatedAt
                      ? new Date(cat.updatedAt).toLocaleDateString()
                      : "N/A"}
                  </td>

                  <td className={styles.actionCell}>
                    <div className={styles.actionButtons}>
                      <span
                        className={styles.actionIcon}
                        onClick={() => handleEdit(cat)}
                      >
                        <FiEdit2 />
                      </span>
                      <span
                        className={styles.actionIcon}
                        onClick={() => cat.id && handleDelete(cat.id)}
                      >
                        <FiTrash2 />
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showPopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.popup}>
            <h4>
              {editingCategory?.id ? "Edit Category" : "Add New Category"}
            </h4>
            <form onSubmit={handleAddOrEdit}>
              <input
                className={styles.input}
                placeholder="Category name"
                value={editingCategory?.name || ""}
                onChange={(e) =>
                  setEditingCategory((prev) =>
                    prev
                      ? {
                          ...prev,
                          name: e.target.value as typeof prev.name,
                        }
                      : null
                  )
                }
                autoFocus
              />
              {error && <div className={styles.error}>{error}</div>}
              <div className={styles.popupActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={resetPopup}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.saveBtn}>
                  {editingCategory?.id ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeletePopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.popup}>
            <h4>Delete Category</h4>
            <p className={styles.deleteMessage}>
              Are you sure you want to delete this category? This action cannot
              be undone.
            </p>
            <div className={styles.popupActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={resetDeletePopup}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

    </div>
  );
};

export default CategoryList;
